/**
 * Upsell Popup — Checkout upsell para tema Impulse
 *
 * Flujo:
 * 1. Intercepta el clic en botones .cart__checkout
 * 2. Obtiene el carrito y carga recomendaciones del primer producto
 * 3. Muestra el modal con las sugerencias
 * 4. El cliente puede añadir productos y luego ir al checkout
 */

(function () {
  'use strict';

  const cfg = window.upsellPopupConfig || {};
  const STORAGE_KEY = 'upsell_popup_shown';
  let allProducts = [];

  // ── Utilidades ──────────────────────────────────────────────

  function formatMoney(cents, format) {
    if (!format) format = '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', amount)
                 .replace('{{amount_no_decimals}}', Math.round(cents / 100))
                 .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then(r => r.json());
  }

  /**
   * Llama a la API de Shopify con intent=complementary.
   * Solo devuelve productos si el merchant los ha definido manualmente
   * en el admin (Búsqueda y descubrimiento → Productos complementarios).
   */
  function getComplementary(productId) {
    return fetch(
      cfg.recommendationsUrl +
      '?product_id=' + productId +
      '&limit=20' +
      '&intent=complementary'
    ).then(r => r.json())
     .then(function(data) {
       return data.products || [];
     });
  }

  /**
   * Itera los productos del carrito en orden y devuelve el primer lote
   * de complementarios que tenga al menos un resultado.
   * Si ninguno tiene complementarios definidos, devuelve array vacío.
   */
  function getComplementaryForCart(cartItems) {
    var ids = cartItems.map(function(i) { return i.product_id; });
    // Eliminar duplicados
    ids = ids.filter(function(id, idx) { return ids.indexOf(id) === idx; });

    return ids.reduce(function(promise, id) {
      return promise.then(function(found) {
        if (found.length > 0) return found; // ya tenemos resultados, no seguir
        return getComplementary(id);
      });
    }, Promise.resolve([]));
  }

  function addToCart(variantId, quantity) {
    return fetch(cfg.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    }).then(r => r.json());
  }

  function goToCheckout() {
    window.location.href = cfg.cartUrl + '/checkout';
  }

  // ── DOM helpers ─────────────────────────────────────────────

  function buildSkeleton() {
    const html = Array(6).fill(0).map(() => `
      <div class="upsell-skeleton">
        <div class="upsell-skeleton__img"></div>
        <div class="upsell-skeleton__line"></div>
        <div class="upsell-skeleton__line upsell-skeleton__line--short"></div>
      </div>
    `).join('');
    return html;
  }

  function buildProductCard(product) {
    const variants = product.variants || [];
    // available puede ser true, false o null/undefined (sin seguimiento de inventario = disponible)
    // Solo consideramos agotado si es EXPLÍCITAMENTE false
    function isAvailable(v) { return v.available !== false; }
    const firstAvailable = variants.find(isAvailable) || variants[0];
    // Producto con variante única sin nombre real (placeholder "Title")
    const isSingleDefault = variants.length === 1 ||
      (product.options && product.options.length === 1 && product.options[0] === 'Title');
    const hasMultipleVariants = variants.length > 1 && !isSingleDefault;

    const price = formatMoney(product.price_min, cfg.moneyFormat);
    const comparePrice = product.compare_at_price_min && product.compare_at_price_min > product.price_min
      ? formatMoney(product.compare_at_price_min, cfg.moneyFormat)
      : null;

    const imageSrc = product.featured_image
      ? product.featured_image.replace(/(\.\w+)$/, '_400x400$1')
      : null;

    const imageHtml = imageSrc
      ? `<img class="upsell-card__image" src="${imageSrc}" alt="${product.title}" loading="lazy" width="400" height="400">`
      : `<div class="upsell-card__image upsell-card__image--placeholder">
           <svg width="60" height="60" viewBox="0 0 60 60" fill="none"><rect width="60" height="60" fill="#eee"/><path d="M20 40l10-14 8 10 5-6 7 10H20z" fill="#ccc"/></svg>
         </div>`;

    const priceHtml = comparePrice
      ? `<span class="upsell-card__price--compare">${comparePrice}</span><span class="upsell-card__price--sale">${price}</span>`
      : `<span>${price}</span>`;

    // Selector de variantes: solo cuando hay 2+ variantes disponibles
    const availableVariants = variants.filter(isAvailable);
    const variantsHtml = availableVariants.length > 1
      ? `<div class="upsell-card__variants">
           <select class="upsell-card__variant-select" aria-label="Seleccionar variante">
             ${availableVariants.map(function(v) {
               const sel = firstAvailable && v.id === firstAvailable.id ? 'selected' : '';
               return '<option value="' + v.id + '" ' + sel + '>' + v.title + '</option>';
             }).join('')}
           </select>
         </div>`
      : '';

    const btnLabel = firstAvailable ? 'Añadir al carrito' : 'Agotado';
    const btnDisabled = firstAvailable ? '' : 'disabled';
    const defaultVariantId = firstAvailable ? firstAvailable.id : (variants[0] ? variants[0].id : '');

    return `
      <div class="upsell-card" data-product-id="${product.id}">
        <a href="/products/${product.handle}" class="upsell-card__image-wrap" tabindex="-1" aria-hidden="true">
          ${imageHtml}
        </a>
        <div class="upsell-card__info">
          <p class="upsell-card__title">
            <a href="/products/${product.handle}">${product.title}</a>
          </p>
          <p class="upsell-card__price">${priceHtml}</p>
        </div>
        ${variantsHtml}
        <button
          class="upsell-card__btn"
          data-variant-id="${defaultVariantId}"
          data-product-title="${product.title}"
          ${btnDisabled}>
          ${btnLabel}
        </button>
      </div>
    `;
  }

  // ── Modal logic ─────────────────────────────────────────────

  const popup  = document.getElementById('UpsellPopup');
  const productsContainer = document.getElementById('UpsellPopupProducts');
  const closeBtn  = document.getElementById('UpsellPopupClose');
  const checkoutBtn = document.getElementById('UpsellPopupCheckout');
  const overlay   = document.getElementById('UpsellPopupOverlay');
  const prevBtn   = document.getElementById('UpsellPopupPrev');
  const nextBtn   = document.getElementById('UpsellPopupNext');
  const pagination = document.getElementById('UpsellPopupPagination');
  const scrollArea = popup && popup.querySelector('.upsell-popup__scroll-area');

  function renderProducts() {
    productsContainer.innerHTML = allProducts.map(buildProductCard).join('');
    if (prevBtn) prevBtn.hidden = true;
    if (nextBtn) nextBtn.hidden = true;
    if (pagination) pagination.hidden = true;
    if (scrollArea) scrollArea.scrollTop = 0;
  }

  if (!popup) return; // snippet not loaded

  function openPopup() {
    popup.hidden = false;
    document.body.style.overflow = 'hidden';
    // Focus management
    setTimeout(() => closeBtn && closeBtn.focus(), 100);
  }

  function closePopup() {
    popup.hidden = true;
    document.body.style.overflow = '';
  }

  // Keyboard trap
  document.addEventListener('keydown', function (e) {
    if (popup.hidden) return;
    if (e.key === 'Escape') closePopup();
  });

  closeBtn  && closeBtn.addEventListener('click',   closePopup);
  overlay   && overlay.addEventListener('click',    closePopup);
  checkoutBtn && checkoutBtn.addEventListener('click', goToCheckout);

  // Add to cart from card buttons
  productsContainer && productsContainer.addEventListener('click', function (e) {
    const btn = e.target.closest('.upsell-card__btn');
    if (!btn || btn.disabled) return;

    const card = btn.closest('.upsell-card');
    const select = card && card.querySelector('.upsell-card__variant-select');
    const variantId = select ? select.value : btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('upsell-card__btn--loading');
    btn.textContent = 'Añadiendo…';

    addToCart(variantId, 1)
      .then(function (response) {
        if (response.status === 422 || response.description) {
          throw new Error(response.description || 'Error al añadir');
        }
        btn.classList.remove('upsell-card__btn--loading');
        btn.classList.add('upsell-card__btn--added');
        btn.textContent = '✓ Añadido';
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(function (err) {
        btn.classList.remove('upsell-card__btn--loading');
        btn.textContent = 'Error, reintentar';
        console.error('[Upsell Popup] addToCart error:', err);
      });
  });

  // Actualizar botón al cambiar variante
  productsContainer && productsContainer.addEventListener('change', function (e) {
    const select = e.target.closest('.upsell-card__variant-select');
    if (!select) return;

    const card = select.closest('.upsell-card');
    const btn = card && card.querySelector('.upsell-card__btn');

    if (btn) {
      btn.dataset.variantId = select.value;
      btn.disabled = false;
      btn.classList.remove('upsell-card__btn--added', 'upsell-card__btn--loading');
      btn.textContent = 'Añadir al carrito';
    }
  });

  // ── Intercept checkout buttons ───────────────────────────────

  function interceptCheckout(e) {
    // If popup already open, let through
    if (!popup.hidden) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    // Primero buscar — solo abrir el popup si hay complementarios definidos
    getCart()
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) {
          return Promise.reject('empty_cart');
        }
        return getComplementaryForCart(cart.items);
      })
      .then(function (products) {
        if (products.length === 0) {
          // Sin complementarios definidos → checkout directo sin popup
          goToCheckout();
          return;
        }
        // Hay productos → mostrar popup ya con contenido (sin skeleton)
        allProducts = products;
        renderProducts();
        openPopup();
      })
      .catch(function (err) {
        // Error de API o carrito vacío → checkout directo
        goToCheckout();
      });
  }

  // Attach to all checkout buttons (cart page + drawer), including dynamically added
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('button[name="checkout"].cart__checkout');
    if (btn) interceptCheckout(e);
  }, true); // capture phase to intercept before theme handlers

})();
